import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loader, Button, StatCard } from "@/components/PageUI";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarCheck, Check, Clock, ShieldQuestion, X } from "lucide-react";

const STATUSES = [
  { key: "present", label: "Present", icon: Check, cls: "bg-emerald-500 text-white border-emerald-500" },
  { key: "late", label: "Late", icon: Clock, cls: "bg-amber-500 text-white border-amber-500" },
  { key: "excused", label: "Excused", icon: ShieldQuestion, cls: "bg-blue-500 text-white border-blue-500" },
  { key: "absent", label: "Absent", icon: X, cls: "bg-red-500 text-white border-red-500" },
];

export default function Attendance() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [players, setPlayers] = useState([]);
  const [marks, setMarks] = useState({});
  const [summary, setSummary] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/teams").then((r) => { setTeams(r.data); if (r.data[0]) setTeamId(r.data[0].id); });
  }, []);

  useEffect(() => {
    if (!teamId) return;
    api.get(`/players?team_id=${teamId}`).then((r) => setPlayers(r.data));
    api.get(`/attendance/summary?team_id=${teamId}`).then((r) => setSummary(r.data));
    setMarks({});
  }, [teamId]);

  const save = async () => {
    const records = players.map((p) => ({ player_id: p.id, status: marks[p.id] || "absent" }));
    setLoading(true);
    try {
      await api.post("/attendance", { team_id: teamId, date, records });
      toast.success("Attendance saved");
      const r = await api.get(`/attendance/summary?team_id=${teamId}`); setSummary(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const sumFor = (pid) => summary.find((s) => s.player_id === pid);
  const teamPct = summary.length ? Math.round(summary.reduce((a, s) => a + s.percentage, 0) / summary.length) : 0;

  if (!teams) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Attendance" subtitle="One tap per athlete. Percentages calculate automatically." testid="attendance-header"
        action={<Button onClick={save} disabled={!players.length || loading} className="brand-gradient text-white border-0 hover:opacity-90" data-testid="save-attendance-btn">{loading ? "Saving..." : "Save attendance"}</Button>} />

      {teams.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="No teams yet" desc="Create a team and add athletes to track attendance." testid="empty-attendance" />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatCard icon={CalendarCheck} label="Team attendance" value={`${teamPct}%`} accent="text-secondary" testid="stat-team-attendance" />
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Team</p>
              <Select value={teamId} onValueChange={setTeamId}><SelectTrigger data-testid="attendance-team-select"><SelectValue /></SelectTrigger>
                <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Date</p>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date-input" />
            </div>
          </div>

          {players.length === 0 ? (
            <EmptyState icon={CalendarCheck} title="No athletes in this team" testid="empty-attendance-players" />
          ) : (
            <div className="rounded-2xl border border-border bg-card divide-y divide-border">
              {players.map((p) => {
                const s = sumFor(p.id);
                return (
                  <div key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4" data-testid={`attendance-row-${p.id}`}>
                    <div className="flex items-center gap-3">
                      {p.photo ? <img src={p.photo} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center text-white text-sm font-semibold">{p.name[0]}</div>}
                      <div>
                        <p className="font-medium text-sm">{p.name}</p>
                        {s && <p className="text-xs text-muted-foreground">{s.percentage}% · {s.present}P {s.late}L {s.absent}A</p>}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {STATUSES.map((st) => (
                        <button key={st.key} onClick={() => setMarks((m) => ({ ...m, [p.id]: st.key }))}
                          data-testid={`mark-${st.key}-${p.id}`}
                          className={`h-9 px-3 rounded-lg border text-xs font-semibold flex items-center gap-1 transition-colors duration-150 ${marks[p.id] === st.key ? st.cls : "border-border text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
                          <st.icon size={14} /> <span className="hidden sm:inline">{st.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
